import { prettyDateTime } from "@app/date";
import {
  cancelFetchEndpointPoll,
  estimateMonthlyCost,
  fetchApp,
  fetchDatabase,
  fetchImageById,
  fetchService,
  getContainerPort,
  getEndpointText,
  getEndpointUrl,
  isTlsOrTcp,
  pollFetchEndpoint,
  requiresAcmeSetup,
  selectAppById,
  selectDatabaseById,
  selectEndpointById,
  selectImageById,
  selectServiceById,
} from "@app/deploy";
import { useLoader, useQuery } from "@app/react";
import { useDispatch, useSelector } from "@app/react";
import {
  appEndpointsUrl,
  appServicesUrl,
  databaseDetailUrl,
  databaseEndpointsUrl,
  endpointDetailActivityUrl,
  endpointDetailCredentialsUrl,
  endpointDetailSettingsUrl,
  endpointDetailSetupUrl,
  endpointDetailUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { setResourceStats } from "@app/search";
import type {
  DeployApp,
  DeployDatabase,
  DeployEndpoint,
  DeployService,
} from "@app/types";
import { useEffect, useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { usePoller } from "../hooks";
import {
  Banner,
  COST_ESTIMATE_TOOLTIP_TEXT,
  CRUMB_VARIANTS,
  Code,
  CopyText,
  CopyTextButton,
  DetailPageHeaderView,
  EndpointUrl,
  Loading,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
  type TabItem,
} from "../shared";
import {
  EndpointStatusDisplay,
  SharedEndpointCell,
} from "../shared/endpoint/util";
import { AppSidebarLayout } from "./app-sidebar-layout";

export function EndpointAppHeaderInfo({
  enp,
  app,
  service,
  isLoading,
}: {
  enp: DeployEndpoint;
  app: DeployApp;
  service: DeployService;
  isLoading: boolean;
}) {
  const txt = getEndpointText(enp);
  const image = useSelector((s) =>
    selectImageById(s, { id: app.currentImageId }),
  );
  const portTxt = getContainerPort(enp, image.exposedPorts);
  const rates = useSelector(schema.costRates.select);
  const cost = estimateMonthlyCost({
    rates,
    endpoints: [{ shared: enp.shared }],
  });

  return (
    <div className="flex flex-col gap-4">
      {!!enp.deletedAt && (
        <Banner className="mt-2 mb-4" variant="warning">
          This endpoint was deprovisioned on {prettyDateTime(enp.deletedAt)}.
        </Banner>
      )}
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src={"/resource-types/logo-vhost.png"}
            className="w-[32px] h-[32px]"
            aria-label="Endpoint"
          />
        }
        nameLabel="Hostname"
        name={txt.hostname}
        nameCopyText={txt.hostname}
        id={enp.id}
        url={
          <div className="flex flex-row items-center gap-2 min-w-0">
            <div className="min-w-0 break-words">
              <EndpointUrl enp={enp} />
            </div>
            <CopyTextButton text={txt.url} className="shrink-0" />
          </div>
        }
        dateLabel="Provisioned"
        createdAt={enp.createdAt}
        cost={cost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Type">
          <Code>{enp.type}</Code>
        </ResourceDetailItem>
        <ResourceDetailItem title="Placement">
          {txt.placement}
        </ResourceDetailItem>
        <ResourceDetailItem title="Service Process">
          <Code>{service.processType}</Code>
        </ResourceDetailItem>
        {isTlsOrTcp(enp) ? (
          <ResourceDetailItem title="Ports">{portTxt}</ResourceDetailItem>
        ) : (
          <ResourceDetailItem title="Port">{portTxt}</ResourceDetailItem>
        )}
        <ResourceDetailItem title="Service ID">
          <CopyText text={service.id} />
        </ResourceDetailItem>
        <ResourceDetailItem title="Using Header Auth">
          {txt.token_header}
        </ResourceDetailItem>
        <ResourceDetailItem title="Resource">
          <Link to={appEndpointsUrl(app.id)}>{app.handle}</Link>
        </ResourceDetailItem>
        <ResourceDetailItem title="Status">
          <EndpointStatusDisplay
            status={enp.deletedAt ? "deprovisioned" : enp.status}
          />
        </ResourceDetailItem>
        <ResourceDetailItem title="Shared">
          <SharedEndpointCell enp={enp} />
        </ResourceDetailItem>
        <ResourceDetailItem title="IP Allowlist">
          {txt.ipAllowlist}
        </ResourceDetailItem>
      </ResourceDetailsTable>
    </div>
  );
}

export function EndpointDatabaseHeaderInfo({
  enp,
  db,
  isLoading,
}: { enp: DeployEndpoint; db: DeployDatabase; isLoading: boolean }) {
  const txt = getEndpointText(enp);
  const rates = useSelector(schema.costRates.select);
  const cost = estimateMonthlyCost({
    rates,
    endpoints: [{ shared: enp.shared }],
  });

  return (
    <div className="flex flex-col gap-4">
      {!!enp.deletedAt && (
        <Banner className="mt-2 mb-4" variant="warning">
          This endpoint was deprovisioned on {prettyDateTime(enp.deletedAt)}.
        </Banner>
      )}
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src={"/resource-types/logo-vhost.png"}
            className="w-[32px] h-[32px]"
            aria-label="Endpoint"
          />
        }
        nameLabel="Hostname"
        name={txt.hostname}
        nameCopyText={txt.hostname}
        id={enp.id}
        url={
          <div className="flex flex-row items-center gap-2 min-w-0">
            <div className="min-w-0 break-words">
              <EndpointUrl enp={enp} />
            </div>
            <CopyTextButton text={getEndpointUrl(enp)} className="shrink-0" />
          </div>
        }
        dateLabel="Provisioned"
        createdAt={enp.createdAt}
        cost={cost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Status">
          <EndpointStatusDisplay
            status={enp.deletedAt ? "deprovisioned" : enp.status}
          />
        </ResourceDetailItem>
        <ResourceDetailItem title="Resource">
          <Link to={databaseEndpointsUrl(db.id)}>{db.handle}</Link>
        </ResourceDetailItem>
        <ResourceDetailItem title="IP Allowlist">
          {txt.ipAllowlist}
        </ResourceDetailItem>
        <ResourceDetailItem title="Placement">
          {txt.placement}
        </ResourceDetailItem>
      </ResourceDetailsTable>
    </div>
  );
}

function EndpointAppHeader({
  enp,
  service,
  isError,
  message,
  meta,
  isLoading,
}: {
  enp: DeployEndpoint;
  service: DeployService;
  isError: boolean;
  message: string;
  meta: Record<string, any>;
  isLoading: boolean;
}) {
  useQuery(fetchApp({ id: service.appId }));
  const app = useSelector((s) => selectAppById(s, { id: service.appId }));
  useQuery(fetchImageById({ id: app.currentImageId }));
  const serviceUrl = appServicesUrl(app.id);
  const tabs: TabItem[] = [
    { name: "Activity", href: endpointDetailActivityUrl(enp.id) },
  ];
  if (!enp.deletedAt)
    tabs.push({ name: "Settings", href: endpointDetailSettingsUrl(enp.id) });
  if (enp.acme && enp.status === "provisioned") {
    tabs.push({
      name: "DNS Configuration",
      href: endpointDetailSetupUrl(enp.id),
    });
  }
  const crumbs = [];
  if (app.id) {
    crumbs.push({
      name: app.handle,
      to: serviceUrl,
      label: "App",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  } else {
    crumbs.push({
      name: "Unknown App",
      to: "#",
      label: "App",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  }

  return (
    <>
      {requiresAcmeSetup(enp) ? (
        <Banner variant="warning" className="mb-4">
          Further steps required to setup custom domain!{" "}
          <Link to={endpointDetailSetupUrl(enp.id)} className="underline">
            Finish setup
          </Link>
        </Banner>
      ) : null}
      <DetailPageHeaderView
        isError={isError}
        message={message}
        meta={meta}
        tabs={tabs}
        breadcrumbs={crumbs}
        titleLabel="Endpoint"
        title={`ID: ${enp.id}`}
        lastBreadcrumbTo={endpointDetailUrl(enp.id)}
        detailsBox={
          <EndpointAppHeaderInfo
            enp={enp}
            app={app}
            service={service}
            isLoading={isLoading}
          />
        }
      />
    </>
  );
}

function EndpointDatabaseHeader({
  enp,
  service,
  isError,
  message,
  meta,
  isLoading,
}: {
  enp: DeployEndpoint;
  service: DeployService;
  isError: boolean;
  message: string;
  meta: Record<string, any>;
  isLoading: boolean;
}) {
  useQuery(fetchDatabase({ id: service.databaseId }));
  const db = useSelector((s) =>
    selectDatabaseById(s, { id: service.databaseId }),
  );
  const dbUrl = databaseDetailUrl(db.id);
  const tabs: TabItem[] = [
    { name: "Activity", href: endpointDetailActivityUrl(enp.id) },
  ];
  if (!enp.deletedAt)
    tabs.push(
      ...[
        {
          name: "Connection URL",
          href: endpointDetailCredentialsUrl(enp.id),
        },
        { name: "Settings", href: endpointDetailSettingsUrl(enp.id) },
      ],
    );

  const crumbs = [];
  if (db.id) {
    crumbs.push({
      name: db.handle,
      to: dbUrl,
      label: "Database",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  } else {
    crumbs.push({
      name: "Unknown Database",
      to: "#",
      label: "Database",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  }

  return (
    <DetailPageHeaderView
      isError={isError}
      message={message}
      meta={meta}
      tabs={tabs}
      breadcrumbs={crumbs}
      titleLabel="Endpoint"
      title={`ID: ${enp.id}`}
      lastBreadcrumbTo={endpointDetailUrl(enp.id)}
      detailsBox={
        <EndpointDatabaseHeaderInfo enp={enp} db={db} isLoading={isLoading} />
      }
    />
  );
}

function EndpointPageHeader() {
  const { id = "" } = useParams();
  const dispatch = useDispatch();
  const action = useMemo(
    () => pollFetchEndpoint({ id, withDeleted: true }),
    [id],
  );
  const cancel = useMemo(() => cancelFetchEndpointPoll(), []);
  const loader = useLoader(action);
  usePoller({ action, cancel });
  const enp = useSelector((s) => selectEndpointById(s, { id }));

  useEffect(() => {
    dispatch(
      setResourceStats({ id, type: "endpoint", deletedAt: enp?.deletedAt }),
    );
  }, [enp?.deletedAt]);

  useQuery(fetchService({ id: enp.serviceId, withDeleted: true }));
  const service = useSelector((s) =>
    selectServiceById(s, { id: enp.serviceId }),
  );

  if (service.appId) {
    return <EndpointAppHeader {...loader} enp={enp} service={service} />;
  }

  if (service.databaseId) {
    return <EndpointDatabaseHeader {...loader} enp={enp} service={service} />;
  }

  return <Loading text="Loading endpoint information..." />;
}

export const EndpointDetailLayout = () => {
  return (
    <AppSidebarLayout
      header={<EndpointPageHeader />}
      mainWrapperDivClassName="overflow-x-hidden"
    >
      <Outlet />
    </AppSidebarLayout>
  );
};
